export type HreflangLink = {
  hrefLang: string;
  href: string;
};

function normalisePathname(pathname: string) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function getUnprefixedPathname(pathname: string, locales: readonly string[]) {
  const localeSlugs = new Set(locales.map(locale => locale.toLowerCase()));
  const pathSegments = pathname.split('/').filter(Boolean);
  const [firstSegment] = pathSegments;

  if (firstSegment && localeSlugs.has(firstSegment.toLowerCase())) {
    pathSegments.shift();
  }

  return normalisePathname(`/${pathSegments.join('/')}`);
}

function buildLocaleHref(
  origin: string,
  pathname: string,
  locale: string,
  defaultLocale: string,
) {
  const normalisedPathname = normalisePathname(pathname);

  if (locale.toLowerCase() === defaultLocale.toLowerCase()) {
    return `${origin}${normalisedPathname}`;
  }

  const suffix = normalisedPathname === '/' ? '' : normalisedPathname;
  return `${origin}/${locale}${suffix}`;
}

export function buildHreflangLinks(
  origin: string | null | undefined,
  pathname: string | null | undefined,
  locales: readonly string[],
  defaultLocale: string | null | undefined,
): HreflangLink[] {
  if (!origin || !pathname || !locales.length || !defaultLocale) {
    return [];
  }

  let parsedOrigin: URL;
  try {
    parsedOrigin = new URL(origin);
  } catch {
    return [];
  }

  const uniqueLocales = Array.from(new Set(locales));
  const unprefixedPathname = getUnprefixedPathname(pathname, [
    ...uniqueLocales,
    defaultLocale,
  ]);
  const links = uniqueLocales.map(locale => ({
    hrefLang: locale,
    href: buildLocaleHref(
      parsedOrigin.origin,
      unprefixedPathname,
      locale,
      defaultLocale,
    ),
  }));

  const hasVerifiedDefaultLocale = uniqueLocales.some(
    locale => locale.toLowerCase() === defaultLocale.toLowerCase(),
  );

  if (!hasVerifiedDefaultLocale) {
    return links;
  }

  return [
    ...links,
    {
      hrefLang: 'x-default',
      href: buildLocaleHref(
        parsedOrigin.origin,
        unprefixedPathname,
        defaultLocale,
        defaultLocale,
      ),
    },
  ];
}
